import React, { useEffect, useState } from "react";
import { wsRequest, onBroadcast } from "./wsClient.js";
import { showToast } from "./toast.js";

const PROVIDER_LABEL = {
  resend: "Resend",
  brevo: "Brevo",
  postmark: "Postmark",
  self_hosted: "Your own SMTP server",
};

const ALL_PROVIDERS = ["resend", "brevo", "postmark", "self_hosted"];

// EMAIL SETTINGS — the fallback chain.
//
// Without this, one provider is ever tried: if it fails the message is
// logged and dropped. Four provider cards on the Providers tab imply
// otherwise.
export default function EmailSettingsPanel({ token, canEdit }) {
  const [config, setConfig] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [order, setOrder] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(null);

  const load = () => {
    wsRequest("admin.emailProvider.get", {}, token)
      .then((c) => {
        setConfig(c);
        setEnabled(!!c.fallbackEnabled);
        setOrder(Array.isArray(c.fallbackOrder) ? c.fallbackOrder : []);
      })
      .catch(() => {});
  };

  useEffect(load, [token]);

  useEffect(
    () =>
      onBroadcast((message) => {
        if (message.type === "email-provider-changed") load();
      }),
    [token]
  );

  if (!config) return <p>Loading…</p>;

  const active = config.active;

  // The providers that can be fallbacks: everything except the active
  // one, which is always tried first and is not part of the order.
  const candidates = ALL_PROVIDERS.filter((p) => p !== active);

  // HAS THIS PROVIDER GOT CREDENTIALS? The same question the send path
  // asks before trying one — `hasKey` for the API providers, a host for
  // a self-hosted server. Both are already in the config read; neither
  // is a secret.
  //
  // A provider without them is listed but cannot be ticked: the chain
  // would skip it anyway, so offering it is offering something that
  // cannot happen.
  const isConfigured = (name) => {
    const creds = config[name] || {};
    return name === "self_hosted" ? !!creds.host : !!creds.hasKey;
  };

  // Chosen, in order, then the rest — so the list shows everything and
  // the ticked ones sit at the top in the order they will be tried.
  const chosen = order.filter((p) => candidates.includes(p));
  const unchosen = candidates.filter((p) => !chosen.includes(p));

  const toggleProvider = (name) => {
    setOrder((prev) => (prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]));
  };

  const onDrop = (target) => {
    if (!dragging || dragging === target) return;
    setOrder((prev) => {
      const next = prev.filter((p) => p !== dragging);
      const at = next.indexOf(target);
      if (at < 0) return prev;
      next.splice(at, 0, dragging);
      return next;
    });
    setDragging(null);
  };

  // UP AND DOWN, because dragging is not always available and sometimes
  // not usable. HTML5 drag-and-drop does not fire on touch at all, and
  // it is fiddly with a mouse — the first version of this did not work
  // because onDragStart never called dataTransfer.setData, which several
  // browsers require before they will begin a drag.
  //
  // The buttons are the reliable path; the drag is the convenience.
  const move = (name, delta) => {
    setOrder((prev) => {
      const at = prev.indexOf(name);
      const to = at + delta;
      if (at < 0 || to < 0 || to >= prev.length) return prev;
      const next = prev.slice();
      next.splice(at, 1);
      next.splice(to, 0, name);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await wsRequest("admin.emailProvider.setFallback", { enabled, order: chosen }, token);
      showToast("Saved.", "success");
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 16, maxWidth: 640 }}>
      <h4 style={{ marginTop: 0 }}>Fallback</h4>

      {!active ? (
        <p style={{ opacity: 0.75, fontSize: 13 }}>
          No email provider is active yet. Choose one on the Providers tab first — a fallback chain
          needs something to fall back <em>from</em>.
        </p>
      ) : (
        <>
          <p style={{ opacity: 0.75, fontSize: 13, marginTop: 0 }}>
            If <strong>{PROVIDER_LABEL[active] || active}</strong> cannot send a message, try the
            providers below in order. Without this, a message that fails is logged and dropped.
          </p>

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={enabled}
              disabled={!canEdit || busy}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Use a fallback provider when the active one fails
          </label>

          <div style={{ opacity: enabled ? 1 : 0.45, pointerEvents: enabled ? "auto" : "none" }}>
            {candidates.some(isConfigured) ? (
              <p style={{ fontSize: 12.5, opacity: 0.75, margin: "0 0 8px" }}>
                Tick the providers to fall back to, then use the arrows — or drag — to set the order
                they are tried in.
              </p>
            ) : (
              <p style={{ fontSize: 12.5, opacity: 0.75, margin: "0 0 8px" }}>
                <strong>No other provider is set up yet.</strong> Add credentials for one under
                Email &rarr; Providers and it can be used here — until then there is nothing to fall
                back to.
              </p>
            )}

            {[...chosen, ...unchosen].map((name) => {
              const isChosen = chosen.includes(name);
              const configured = isConfigured(name);
              const position = isChosen ? chosen.indexOf(name) + 1 : null;
              // A PROVIDER IN THE ORDER THAT HAS SINCE LOST ITS
              // CREDENTIALS. The stored order deliberately keeps it —
              // clearing a key should not silently reshuffle the rest —
              // so it is shown, in place, saying why it will be skipped.
              const stale = isChosen && !configured;
              return (
                <div
                  key={name}
                  draggable={isChosen && canEdit && configured}
                  onDragStart={(e) => {
                    setDragging(name);
                    // REQUIRED. Without a payload several browsers
                    // refuse to start the drag at all, which is why the
                    // first version of this did nothing.
                    e.dataTransfer.effectAllowed = "move";
                    try {
                      e.dataTransfer.setData("text/plain", name);
                    } catch {
                      // Some browsers refuse setData outside a real
                      // drag; the buttons still work.
                    }
                  }}
                  onDragOver={(e) => {
                    // preventDefault is what marks this a valid drop
                    // target — without it the browser refuses the drop.
                    if (isChosen) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }
                  }}
                  onDrop={() => onDrop(name)}
                  onDragEnd={() => setDragging(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    border: "1px solid var(--border-color)",
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 6,
                    background: dragging === name ? "var(--bg-panel-alt)" : "transparent",
                    cursor: isChosen && canEdit && configured ? "grab" : "default",
                    opacity: configured ? 1 : 0.55,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChosen}
                    disabled={!canEdit || busy || !configured}
                    onChange={() => toggleProvider(name)}
                  />
                  <span style={{ fontSize: 13, flex: 1 }}>
                    {position !== null && (
                      <strong style={{ opacity: 0.6, marginRight: 6 }}>{position}.</strong>
                    )}
                    {PROVIDER_LABEL[name] || name}
                    {!configured && (
                      <span style={{ opacity: 0.75, marginLeft: 8, fontSize: 12 }}>
                        — no credentials saved.{" "}
                        {stale
                          ? "Still in the order and will be skipped until it is set up."
                          : "Set it up under Email \u2192 Providers to use it here."}
                      </span>
                    )}
                  </span>
                  {isChosen && (
                    <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <button
                        type="button"
                        disabled={!canEdit || busy || position === 1}
                        onClick={() => move(name, -1)}
                        title="Move up"
                        style={{ fontSize: 12, padding: "2px 7px", cursor: "pointer" }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        disabled={!canEdit || busy || position === chosen.length}
                        onClick={() => move(name, 1)}
                        title="Move down"
                        style={{ fontSize: 12, padding: "2px 7px", cursor: "pointer" }}
                      >
                        ↓
                      </button>
                      <span style={{ opacity: 0.4, fontSize: 14, marginLeft: 2 }}>⠿</span>
                    </span>
                  )}
                </div>
              );
            })}

            {chosen.length === 0 && (
              <p style={{ fontSize: 12.5, opacity: 0.7 }}>
                Nothing chosen — a failed message will be logged and dropped, as it is today.
              </p>
            )}
          </div>

          <p style={{ fontSize: 12.5, opacity: 0.7, marginTop: 12 }}>
            A provider with no credentials is skipped rather than tried. When a fallback is used, it
            is reported in Issues — otherwise the chain quietly covers for a provider that has
            stopped working.
          </p>

          <p style={{ fontSize: 12.5, opacity: 0.7 }}>
            <strong>The sender address changes with the provider.</strong> A message sent through a
            fallback arrives from whatever address that provider is configured with.
          </p>

          <button type="button" disabled={!canEdit || busy} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      )}
    </div>
  );
}
